import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal edit dialog for song, beat, or project.
 * Preserves audio, versions, checklist, sessions and every relationship.
 */
export function EditItemDialog({ open, onOpenChange, kind, item, onSaved }) {
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    if (kind === "song") {
      setForm({
        name: item.name || "",
        bpm: item.bpm || "",
        type: item.project_id ? "project" : "single",
        project_id: item.project_id || "",
      });
      api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    } else if (kind === "beat") {
      setForm({ name: item.name || "", bpm: item.bpm || "", notes: item.notes || "" });
    } else if (kind === "project") {
      setForm({ name: item.name || "", type: item.type || "ep" });
    }
  }, [open, item, kind]);

  if (!item) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      let path;
      let body = {};
      if (kind === "song") {
        path = `/songs/${item.id}`;
        body.name = form.name.trim();
        body.bpm = form.bpm ? parseInt(form.bpm) : null;
        body.project_id = form.type === "project" ? (form.project_id || null) : null;
      } else if (kind === "beat") {
        path = `/beats/${item.id}`;
        body.name = form.name.trim();
        body.bpm = form.bpm ? parseInt(form.bpm) : null;
        body.notes = form.notes || "";
      } else if (kind === "project") {
        path = `/projects/${item.id}`;
        body.name = form.name.trim();
        body.type = form.type;
      }
      await api.patch(path, body);
      toast.success("Saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141416] border-white/10 text-white" data-testid="edit-item-dialog">
        <DialogHeader>
          <DialogTitle>Edit {kind === "song" ? "song" : kind === "beat" ? "beat" : "project"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input value={form.name || ""} onChange={set("name")}
              className="bg-black/40 border-white/10 mt-1" data-testid="edit-name" />
          </div>
          {kind === "song" && (
            <>
              <div>
                <Label className="text-xs text-zinc-400">Type</Label>
                <RadioGroup value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))} className="grid grid-cols-2 gap-2 mt-1">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === "single" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="single" /> <span className="text-sm">Single</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === "project" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                    <RadioGroupItem value="project" /> <span className="text-sm">In Project</span>
                  </label>
                </RadioGroup>
              </div>
              {form.type === "project" && (
                <div>
                  <Label className="text-xs text-zinc-400">Project</Label>
                  <Select value={form.project_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                    <SelectTrigger className="bg-black/40 border-white/10 mt-1" data-testid="edit-project-select"><SelectValue placeholder="Choose project" /></SelectTrigger>
                    <SelectContent className="bg-[#141416] border-white/10 text-white">
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-400">BPM</Label>
                <Input value={form.bpm || ""} onChange={(e) => setForm((f) => ({ ...f, bpm: e.target.value.replace(/\D/g, "") }))}
                  inputMode="numeric" className="bg-black/40 border-white/10 mt-1" />
              </div>
            </>
          )}
          {kind === "beat" && (
            <>
              <div>
                <Label className="text-xs text-zinc-400">BPM</Label>
                <Input value={form.bpm || ""} onChange={(e) => setForm((f) => ({ ...f, bpm: e.target.value.replace(/\D/g, "") }))}
                  inputMode="numeric" className="bg-black/40 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Notes</Label>
                <Input value={form.notes || ""} onChange={set("notes")}
                  className="bg-black/40 border-white/10 mt-1" />
              </div>
            </>
          )}
          {kind === "project" && (
            <div>
              <Label className="text-xs text-zinc-400">Type</Label>
              <RadioGroup value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))} className="grid grid-cols-2 gap-2 mt-1">
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === "ep" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                  <RadioGroupItem value="ep" /> <span className="text-sm">EP</span>
                </label>
                <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${form.type === "album" ? "border-violet-500 bg-violet-500/10" : "border-white/10"}`}>
                  <RadioGroupItem value="album" /> <span className="text-sm">Album</span>
                </label>
              </RadioGroup>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-400">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-violet-600 hover:bg-violet-500 rounded-full" data-testid="edit-submit">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
